import { useRef } from "react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { Button } from "@/components/ui/button";
import { Camera, Printer, ArrowLeft, DeviceMobile } from "@phosphor-icons/react";
import { useTranslation } from "@/contexts/LanguageContext";
import { usePDFExport } from "@/utils/pdfExport";

export default function QRPosterPage() {
  const { lang } = useTranslation();
  const printRef = useRef(null);

  const url =
    process.env.REACT_APP_PUBLIC_URL ||
    (typeof window !== "undefined"
      ? window.location.origin
      : "https://dslradvisor.com");
  const hostLabel = url.replace(/^https?:\/\//, "");

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `dslr-advisor-qr-poster-${lang}.pdf`,
    [lang, url]
  );

  const copy =
    lang === "es"
      ? {
          back: "Volver",
          download: "Descargar Poster",
          generating: "Generando...",
          title: "DSLR Advisor",
          tagline: "Tu compañero para fotografía manual",
          scan_label: "Escanea para abrir en tu teléfono",
          or_visit: "O visita",
          ios_title: "En iPhone (Safari)",
          ios_steps: [
            "Toca el botón Compartir",
            'Elige "Añadir a pantalla de inicio"',
            'Confirma con "Añadir"',
          ],
          android_title: "En Android (Chrome)",
          android_steps: [
            'Toca el banner "Instalar DSLR Advisor"',
            "O: menú 3 puntos → Instalar app",
            "Confirma — aparece en tu pantalla de inicio",
          ],
          footer:
            "Una vez instalado, DSLR Advisor se abre como una app nativa — sin barras del navegador.",
          credit: "Creado por Hector Rivera",
        }
      : {
          back: "Back",
          download: "Download Poster",
          generating: "Generating...",
          title: "DSLR Advisor",
          tagline: "Your companion for mastering manual photography",
          scan_label: "Scan to open on your phone",
          or_visit: "Or visit",
          ios_title: "On iPhone (Safari)",
          ios_steps: [
            "Tap the Share button",
            'Choose "Add to Home Screen"',
            'Confirm with "Add"',
          ],
          android_title: "On Android (Chrome)",
          android_steps: [
            'Tap the "Install DSLR Advisor" banner',
            "Or: 3-dot menu → Install app",
            "Confirm — it appears on your home screen",
          ],
          footer:
            "Once installed, DSLR Advisor opens as a native-feeling app — no browser bars.",
          credit: "Built by Hector Rivera",
        };

  return (
    <>
      <div className="print:hidden max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2 flex items-center justify-between">
        <Link to="/presenter">
          <Button
            variant="ghost"
            className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
            data-testid="qr-poster-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {copy.back}
          </Button>
        </Link>
        <Button
          onClick={download}
          disabled={!ready}
          className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
          data-testid="qr-poster-download-btn"
        >
          <Printer className="w-4 h-4 mr-1.5" />
          {generating ? copy.generating : copy.download}
        </Button>
      </div>

      <div
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 print:px-0 print:py-0 print:max-w-none"
        data-testid="qr-poster-page"
      >
        {/* A4-proportioned poster (~1:1.414). On-screen shows a preview card,
            the PDF export captures the same content. */}
        <div
          ref={printRef}
          className="bg-white text-black rounded-xl shadow-xl overflow-hidden"
          style={{ aspectRatio: "1 / 1.414" }}
        >
          <div className="h-full w-full flex flex-col p-10 sm:p-14">
            {/* Top brand row */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#0A0A0A] flex items-center justify-center">
                <Camera weight="fill" className="w-7 h-7 text-[#F5A524]" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-amber-600 font-semibold">
                  {lang === "es" ? "Clase de Fotografía" : "Photography Class"}
                </p>
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                  {copy.title}
                </h1>
              </div>
            </div>

            <p className="text-base text-gray-700 leading-relaxed mb-6 border-b border-gray-200 pb-5">
              {copy.tagline}
            </p>

            {/* Big QR + URL */}
            <div className="flex flex-col items-center mb-6">
              <div className="bg-white p-4 border-4 border-[#F5A524] rounded-2xl shadow-lg">
                <QRCode
                  value={url}
                  size={240}
                  level="H"
                  bgColor="#FFFFFF"
                  fgColor="#0A0A0A"
                  data-testid="qr-poster-code"
                />
              </div>
              <p className="mt-4 text-base font-semibold text-gray-900">
                {copy.scan_label}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {copy.or_visit}{" "}
                <span className="font-mono text-amber-700 font-semibold">
                  {hostLabel}
                </span>
              </p>
            </div>

            {/* Install instructions (two columns) */}
            <div className="grid grid-cols-2 gap-5 mb-6">
              <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DeviceMobile weight="fill" className="w-4 h-4 text-amber-600" />
                  <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider">
                    {copy.ios_title}
                  </h3>
                </div>
                <ol className="text-sm text-gray-800 leading-relaxed list-decimal ml-5 space-y-1">
                  {copy.ios_steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              </div>
              <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DeviceMobile weight="fill" className="w-4 h-4 text-amber-600" />
                  <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider">
                    {copy.android_title}
                  </h3>
                </div>
                <ol className="text-sm text-gray-800 leading-relaxed list-decimal ml-5 space-y-1">
                  {copy.android_steps.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              </div>
            </div>

            <p className="text-xs text-gray-600 italic text-center mb-4">
              {copy.footer}
            </p>

            {/* Footer */}
            <div className="mt-auto border-t border-gray-200 pt-4 flex items-center justify-between text-[11px] text-gray-500">
              <span>{copy.credit}</span>
              <span className="font-mono">{hostLabel}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
